import React from 'react';
import { useHints } from '../../hints/HintContext';
import { HINTS, HINT_MAP } from '../../hints/hints.config';
import TokenIcon from '../TokenIcon';
import './DiscoveryModal.css';

function DiscoveryIcon({ name }) {
  switch (name) {
    case 'sword':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m14 5 5 5" />
          <path d="m12 7 5 5" />
          <path d="m4 20 6-6" />
          <path d="m8 12 4 4" />
          <path d="m3 21 5-1 9-9-4-4-9 9z" />
        </svg>
      );
    case 'pencil':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
        </svg>
      );
    case 'target':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v3" />
          <path d="M12 19v3" />
          <path d="M2 12h3" />
          <path d="M19 12h3" />
        </svg>
      );
    case 'flask':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M10 3v5l-5 8a3 3 0 0 0 2.6 4.5h8.8A3 3 0 0 0 19 16l-5-8V3" />
          <path d="M8 11h8" />
        </svg>
      );
    case 'crown':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m3 18 2-9 7 5 7-5 2 9z" />
          <path d="M5 21h14" />
        </svg>
      );
    case 'map-pin':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 21s6-5.2 6-11a6 6 0 1 0-12 0c0 5.8 6 11 6 11Z" />
          <circle cx="12" cy="10" r="2.5" />
        </svg>
      );
    case 'gem':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m6 5 3-3h6l3 3-6 17z" />
          <path d="M3 8h18" />
        </svg>
      );
    case 'book':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v17H6.5A2.5 2.5 0 0 0 4 22Z" />
          <path d="M8 7h8" />
          <path d="M8 11h8" />
        </svg>
      );
    case 'upload':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 16V4" />
          <path d="m7 9 5-5 5 5" />
          <path d="M4 20h16" />
        </svg>
      );
    case 'shield':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3 5 6v5c0 5 3.4 8.3 7 10 3.6-1.7 7-5 7-10V6z" />
        </svg>
      );
    case 'trophy':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 4h8v4a4 4 0 0 1-8 0z" />
          <path d="M6 6H4a2 2 0 0 0 2 5" />
          <path d="M18 6h2a2 2 0 0 1-2 5" />
          <path d="M12 12v4" />
          <path d="M8 20h8" />
        </svg>
      );
    case 'flag':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 21V4" />
          <path d="M5 5h10l-2 4 2 4H5" />
        </svg>
      );
    case 'map':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 6.5 9 4l6 2 5-2v13.5L15 20l-6-2-5 2z" />
          <path d="M9 4v14" />
          <path d="M15 6v14" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="8" />
        </svg>
      );
  }
}

export default function DiscoveryModal({ hintId, onConfirm, onClose }) {
  const def = HINT_MAP[hintId];
  const { onboardingCount, onboardingDiscovered } = useHints();

  if (!def) return null;

  const hintIndex = HINTS.findIndex(hint => hint.id === hintId);
  const phaseLabel = def.onboarding === false ? 'Passive Discovery' : hintIndex < 10 ? 'Phase 1' : 'Phase 2';
  const progress = onboardingCount > 0 ? (onboardingDiscovered / onboardingCount) * 100 : 0;

  return (
    <div className="discovery-overlay" onClick={onClose}>
      <div className={`discovery-modal discovery-modal--${def.category}`} onClick={(event) => event.stopPropagation()}>
        <div className="discovery-topline">
          <div className={`discovery-badge discovery-badge--${def.category}`}>
            {def.category.toUpperCase()}
          </div>
          <div className="discovery-phase">{phaseLabel}</div>
        </div>

        <div className={`discovery-icon discovery-icon--${def.category}`}>
          <DiscoveryIcon name={def.icon} />
        </div>

        <h2 className="discovery-title">{def.title}</h2>
        <p className="discovery-body">{def.body}</p>

        <div className="discovery-rewards">
          <div className="discovery-pill discovery-pill--tokens">
            <TokenIcon size={12} className="discovery-pill-icon" />
            <span className="discovery-pill-value">+{def.tokens}</span>
          </div>
          <div className="discovery-pill discovery-pill--xp">
            <span className="discovery-pill-value">+{def.xp} XP</span>
          </div>
        </div>

        <button className="discovery-confirm-btn" onClick={onConfirm}>
          Claim
        </button>

        <div className="discovery-progress">
          <div className="discovery-progress-track">
            <div className="discovery-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="discovery-progress-label">
            {onboardingDiscovered} of {onboardingCount}
          </span>
        </div>
      </div>
    </div>
  );
}
